import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { ErrorParser } from '@app/classes';
import { JournalEntryReportPayPeriodFilterOption } from '@app/models/payroll/journal-entry-report-pay-period-filter-option.model';
import { Payroll } from '@app/models/payroll/payroll.model';
import { Schedule } from '@app/models/payroll/schedule.model';
import { FilterPayrollReportForm } from '@app/modules/forms/reporting/components';
import { PayrollResources } from '@app/modules/payroll/payroll.resources';
import { AuthService, NotifyService } from '@app/services';
import { Report } from '@reporting/services/queries/report';
import moment from 'moment';
import { JournalEntryAdditionalReportFields } from '../../interfaces/additional-report-fields.interface';
import { PayrollReportFilterDates } from '../../interfaces/payroll-report-filter-dates.interface';
import { ReportFilter } from '../../interfaces/report-filter.interface';
import { JournalEntryReportService } from '../../services/journal-entry-report.service';

@Component({
    templateUrl: './journal-entry-report.template.html',
    styleUrls: ['./journal-entry-report.style.scss'],
})
export class JournalEntryReportView implements OnInit {
    isLoading = true;
    journalEntryReport: Report<JournalEntryAdditionalReportFields> | null = null;
    journalEntryReportPayPeriodOptions: JournalEntryReportPayPeriodFilterOption[] = [];

    startAt: Date | null = null;
    endAt: Date | null = null;
    schedule: Schedule | null = null;
    displayJournalEntryReportFilterModal = false;
    displayChequeDate = false;
    currentFilters: ReportFilter[] = [];
    paidPayrolls: Payroll[] = [];

    constructor(
        private journalEntryReportService: JournalEntryReportService,
        private notify: NotifyService,
        private http: HttpClient,
        private auth: AuthService
    ) {}

    async ngOnInit(): Promise<void> {
        try {
            await this.loadSchedule();
            [this.paidPayrolls] = await Payroll.param('company', this.auth.company.id).where('states', ['paid']).get();
            if (this.paidPayrolls.length) {
                this.journalEntryReport = await this.journalEntryReportService.fetchJournalEntryReports();
            }
        } catch (error) {
            this.notify.error(error);
        }
        this.isLoading = false;
    }

    getInitialDates(_schedule?: Schedule): [Date, Date] {
        return [moment().startOf('year').toDate(), moment().startOf('day').toDate()];
    }

    hideOrDisplayJournalEntryReportFilterModal(displayJournalEntryReportFilterModal: boolean): void {
        this.displayJournalEntryReportFilterModal = displayJournalEntryReportFilterModal;
    }

    async onExport(contentType: string): Promise<void> {
        if (!this.currentFilters.length) {
            this.currentFilters.push({
                name: 'journalEntryReportId',
                value: this.journalEntryReport.additionalReportFields.journalEntryReportId,
            });
        }
        const exportFilter = { name: 'export', value: 'true' };
        this.currentFilters.push(exportFilter);
        try {
            await this.journalEntryReport.filters(this.currentFilters).export(contentType);
            this.currentFilters.pop();
        } catch (error) {
            this.notify.error(ErrorParser.parse(error));
        }
    }

    async onFilter(dates: PayrollReportFilterDates, form: FilterPayrollReportForm): Promise<void> {
        const isPayPeriodFilter =
            form.filter === 'payPeriodsJournalEntryReport' && form.selectedJournalEntryReportPayPeriod !== null;
        this.setDates(dates.startAt, dates.endAt);
        this.currentFilters = this.buildJournalEntryReportFilters(isPayPeriodFilter, form);
        try {
            this.journalEntryReport = await this.journalEntryReport.filters(this.currentFilters).get();
            this.hideOrDisplayJournalEntryReportFilterModal(false);
        } catch (error) {
            this.notify.error(ErrorParser.parse(error));
        }
    }

    async fetchJournalEntryReportPayPeriodOptions(year: string): Promise<void> {
        try {
            this.journalEntryReportPayPeriodOptions =
                await this.journalEntryReportService.fetchJournalEntryReportPayPeriodOptions(year);
        } catch (error) {
            this.notify.error(ErrorParser.parse(error));
        }
    }

    private setDates(startAt: Date, endAt: Date): void {
        this.startAt = startAt;
        this.endAt = endAt;
    }

    private async loadSchedule(): Promise<void> {
        try {
            const res = await this.http.get(await this.getSchedulePath()).toPromise();
            this.schedule = new Schedule(res['data']['attributes']);
            const [startAt, endAt] = this.getInitialDates(this.schedule);
            this.setDates(startAt, endAt);
        } catch (err) {
            this.notify.error(err.message || 'reporting.payroll.couldNotLoadSchedule');
            this.isLoading = false;
        }
    }

    private buildJournalEntryReportFilters(isPayPeriodFilter: boolean, form: FilterPayrollReportForm): ReportFilter[] {
        if (!isPayPeriodFilter) {
            this.displayChequeDate = false;
            return [
                { name: 'startAt', value: moment(this.startAt).format('YYYY-MM-DD') },
                { name: 'endAt', value: moment(this.endAt).format('YYYY-MM-DD') },
            ];
        }
        this.displayChequeDate = true;
        return [{ name: 'journalEntryReportId', value: form.selectedJournalEntryReportPayPeriod?.id }];
    }

    private getSchedulePath(): string {
        return PayrollResources.Schedule.replace(':company', this.auth.company.id.toString());
    }
}
